/**
 * @fileoverview The words filed against a photo, and the rules for changing them.
 *
 * A caption and a journal entry are the same row in this app, which makes
 * "save the words" more delicate than it sounds: writing where there were
 * none creates a note, rewriting changes it in place rather than growing a
 * near-duplicate, and clearing the box unlinks the picture without deleting
 * somebody's diary. All three rules used to live inline in a nine-hundred
 * line dialog, next to a camera.
 *
 * Where: EditPhotoDialog and UploadPhotoDialog.
 * API: GET /notes/:plantObjId or /locations/:id/journal (to find the
 * photo's note); POST to the same to write one; PUT /notes/:noteId or
 * /locations/:id/journal/:noteId to rewrite it.
 * Settings: none.
 * Words (en/es): none.
 *
 * @module usePhotoNote
 */

import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getNotesFor, createNoteFor, updateNoteFor } from '@/shared/api/notes';
import type { Subject } from '@/shared/api/subject';
import { applyNotesToPlantsCache } from '../../../lib/plantNotesCache';
import { PlantPhoto } from '@/shared/types/photo';
import { debugLog } from '@/config/config';

/** The box's words, the save rules, and the error the dialog shows under it. */
export interface PhotoNote {
  /** What is in the box right now. */
  value: string;
  /** Replaces the words in the box; nothing is saved until the photo is. */
  setValue: (words: string) => void;
  /** What went wrong saving, in words, or null. */
  error: string | null;
  /** Set by the dialog, which also reports failures that are not the note's. */
  setError: (message: string | null) => void;
  /**
   * Writes the words for a photo that already exists.
   *
   * @returns The note_id to send with the photo update: a number to link, null
   *   to unlink, and undefined to say nothing about notes at all — which is
   *   what an unknown starting state has to mean, since an empty box during
   *   the fetch would otherwise read as "cleared" and unlink words nobody
   *   touched.
   */
  saveForExisting: () => Promise<number | null | undefined>;
  /**
   * Writes the words for a photo about to be created.
   *
   * Done before the upload so the picture can be created already pointing at
   * them; the other order leaves a window where the picture has no words and a
   * failure there strands it that way.
   *
   * @returns The new note's id, or null when nothing was written.
   * @throws When the note could not be saved, so the caller can stop.
   */
  saveForNew: () => Promise<number | null>;
  /** Brings the card's copy of this plant's notes back in step. */
  sync: () => Promise<void>;
  /** Forgets everything, for a dialog that has closed. */
  reset: () => void;
}

/**
 * The words for one photo, read when the dialog opens and written when it saves.
 *
 * @param subject - The plant (or location) these words belong to, whatever
 *   happens to the photo. Notes are a plant's, not a picture's.
 * @param photo - The photo being edited, or undefined when uploading a new
 *   one. Its existing words are fetched when it has any.
 * @param isOpen - Fetching is deferred until the dialog is actually open.
 * @returns The box's words and the save rules (PhotoNote).
 */
export function usePhotoNote(
  subject: Subject,
  photo: PlantPhoto | undefined,
  isOpen: boolean
): PhotoNote {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** The note this photo already points at, so an edit changes it. */
  const [existingNoteId, setExistingNoteId] = useState<number | null>(null);
  /**
   * Whether the photo's existing words are known yet.
   *
   * An empty box means two different things while a fetch is in flight —
   * "there are no words" and "we have not looked yet" — and acting on the
   * second would change a note nobody touched.
   */
  const [isKnown, setIsKnown] = useState(false);

  /** Empties the box and forgets what the photo had. */
  const reset = useCallback(() => {
    setValue('');
    setError(null);
    setExistingNoteId(null);
    setIsKnown(false);
  }, []);

  /*
   * Fill the box with the words this photo already has.
   *
   * The photo carries only the note's id, so the wording has to be looked up
   * among the plant's notes. A photo with no note simply opens empty, and
   * typing in it writes the first one.
   */
  /*
   * Keyed on the photo's id and note id and the subject's kind and id, not on
   * the objects: a refetch (switching apps, the phone waking) hands back new
   * ones, and reading the note again then put its saved words back over what
   * the gardener was typing.
   */
  const photoId = photo?.photo_id;
  const photoNoteId = photo?.note_id ?? null;
  const subjectKind = subject.kind;
  const subjectId = subject.id;
  useEffect(() => {
    if (!isOpen || photoId === undefined) return;

    const noteId = photoNoteId;
    if (!noteId) {
      // Nothing to fetch, so the note state is already known.
      setExistingNoteId(null);
      setIsKnown(true);
      return;
    }

    let cancelled = false;
    void getNotesFor({ kind: subjectKind, id: subjectId } as Subject)
      .then((notes) => {
        if (cancelled) return;
        const note = notes.find((n) => n.note_id === noteId);
        /*
         * A photo can point at a note that is no longer there, from before
         * deleting a note cleared the photos describing it. Treat that as a
         * photo with no words rather than trying to rewrite something that
         * does not exist — which failed with a 404 and told the gardener their
         * note could not be saved.
         */
        setExistingNoteId(note ? noteId : null);
        setValue(note?.content ?? '');
        setIsKnown(true);
      })
      .catch((err) => {
        debugLog('usePhotoNote', 'Could not read the photo note:', err);
        // Unknown rather than absent: leave the note alone on save.
        setIsKnown(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, photoId, photoNoteId, subjectKind, subjectId]);

  /** Re-reads the notes and writes them into whichever cache the card reads. */
  const sync = useCallback(async (): Promise<void> => {
    try {
      const fresh = await getNotesFor(subject);
      if (subject.kind === 'plant') applyNotesToPlantsCache(queryClient, subject.id, fresh);
      else queryClient.setQueryData(['notes', subject.kind, subject.id], fresh);
    } catch (err) {
      // A stale caption until the next refetch, never a lost photo, so this
      // stays out of the way of the save it follows.
      debugLog('usePhotoNote', 'Could not refresh the plant notes:', err);
    }
  }, [queryClient, subject]);

  /** Create, rewrite, unlink or leave alone; see PhotoNote.saveForExisting. */
  const saveForExisting = useCallback(async (): Promise<number | null | undefined> => {
    const words = value.trim();

    if (!isKnown) return undefined;
    if (words && existingNoteId) {
      await updateNoteFor(subject, existingNoteId, words);
      return undefined;
    }
    if (words) return createNoteFor(subject, words);
    if (existingNoteId) return null;
    return undefined;
  }, [value, isKnown, existingNoteId, subject]);

  /** Writes a first note when there are words; see PhotoNote.saveForNew. */
  const saveForNew = useCallback(async (): Promise<number | null> => {
    const words = value.trim();
    if (!words) return null;
    return createNoteFor(subject, words);
  }, [value, subject]);

  return { value, setValue, error, setError, saveForExisting, saveForNew, sync, reset };
}
